import { Select, Button2, Radio, Button1 } from "../index";
import { Input, DatePicker } from "antd";
import plusIcon from "../../assets/icons/plus-circle-white.png";
import refreshIcon from "../../assets/icons/refresh-icon.png";
import downIcon from "../../assets/icons/chevron-down.png";
import searchIcon from "../../assets/icons/search-icon.png";
import { useState, useEffect, useMemo } from "react";

import swiftIcon from "../../assets/temp-icons/swift-icon.png";
import fedwireIcon from "../../assets/temp-icons/fedwire.png";
import infoIcon from "../../assets/icons/info-icon.png";
import ExistingRecipientReview from "./ExistingRecipientReview";
import { useNavigate } from "react-router-dom";

import { useAccounts, useBeneficiaries } from "../../data/live";
import { transfersApi, conversionsApi } from "../../api/endpoints";
import FeeBreakdown from "../fee-breakdown/FeeBreakdown";

const ExistingRecipient = ({
  sourceAccountId: propSourceAccountId,
  sourceAccount: propSourceAccount,
  onSourceAccountChange,
}) => {
  const navigate = useNavigate();
  const { accounts } = useAccounts();
  const { beneficiaries } = useBeneficiaries();

  const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState("");
  const [internalSourceAccountId, setInternalSourceAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [quote, setQuote] = useState(null);
  const [indicativeRate, setIndicativeRate] = useState(null);
  const [index, setIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const sourceAccountId = propSourceAccountId || internalSourceAccountId;
  const setSourceAccountId = propSourceAccountId
    ? onSourceAccountChange || (() => {})
    : setInternalSourceAccountId;

  const [selectedCard, setSelectedCard] = useState({
    cardInfo: {},
  });

  useEffect(() => {
    if (
      !propSourceAccountId &&
      accounts.length > 0 &&
      !internalSourceAccountId
    ) {
      setInternalSourceAccountId(accounts[0].id);
    }
  }, [accounts, internalSourceAccountId, propSourceAccountId]);

  useEffect(() => {
    if (beneficiaries.length > 0 && !selectedBeneficiaryId) {
      setSelectedBeneficiaryId(beneficiaries[0].id);
    }
  }, [beneficiaries, selectedBeneficiaryId]);

  const filteredBeneficiaries = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return beneficiaries;
    return beneficiaries.filter((b) => {
      const name = (b.fullName || b.businessName || "").toLowerCase();
      const email = (b.email || "").toLowerCase();
      const nickname = (b.nickname || "").toLowerCase();
      const mobile = (b.mobile || "").toLowerCase();
      return (
        name.includes(q) ||
        email.includes(q) ||
        nickname.includes(q) ||
        mobile.includes(q)
      );
    });
  }, [beneficiaries, searchTerm]);

  const selectedBeneficiary = beneficiaries.find(b => b.id === selectedBeneficiaryId);
  const sourceAccount = propSourceAccount || accounts.find(a => a.id === sourceAccountId);

  // Indicative mid-market rate, fetched whenever both currencies are picked,
  // even before the user types an amount. Mirrors the NewConversion pattern so
  // the page always shows "1 AUD = X USD" instead of "No quote".
  const [rateUnavailable, setRateUnavailable] = useState(false);
  useEffect(() => {
    const src = sourceAccount?.currency || "AUD";
    const dst = selectedBeneficiary?.bankAccounts?.[0]?.currency || "USD";
    if (src === dst) {
      setIndicativeRate(null);
      setRateUnavailable(false);
      return;
    }
    let cancelled = false;
    setRateUnavailable(false);
    conversionsApi
      .rate(src, dst)
      .then((r) => {
        if (!cancelled) {
          if (r && r.midRate && Number(r.midRate) > 0) {
            setIndicativeRate(r);
            setRateUnavailable(false);
          } else {
            setIndicativeRate(null);
            setRateUnavailable(true);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIndicativeRate(null);
          setRateUnavailable(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sourceAccount?.currency, selectedBeneficiary?.bankAccounts]);

  // Live binding quote, fetched once the user enters an amount.
  useEffect(() => {
    if (!selectedBeneficiaryId || !sourceAccountId || !amount || isNaN(amount) || amount <= 0) {
      setQuote(null);
      return;
    }

    const destCurrency = selectedBeneficiary?.bankAccounts?.[0]?.currency || "USD";

    transfersApi.quote({
      sourceAccountId,
      sourceAmount: Number(amount),
      destCurrency,
      transferType: "INTERNATIONAL"
    }).then(q => {
      setQuote(q);
    }).catch(err => {
      console.error("Quote error:", err);
      setQuote(null);
    });
  }, [selectedBeneficiaryId, sourceAccountId, amount, selectedBeneficiary?.bankAccounts]);

  // Display rate prefers the live binding quote, falls back to indicative.
  const displayRate =
    quote?.midRate ?? quote?.exchangeRate ?? indicativeRate?.midRate ?? null;

  const tranferMethodOptions = [
    {
      icon: swiftIcon,
      name: "SWIFT",
    },
    {
      icon: fedwireIcon,
      name: "Fedwire",
    },
  ];

  const onCardSelect = (key) => {
    setSelectedCard({ key: key, cardInfo: tranferMethodOptions[key] });
  };

  return (
    <>
      {index === 0 ? (
        <div className="new-transfer-card row p-3 mt-4 mx-0 justify-content-between rounded-3 border">
          <div className="d-flex align-items-center justify-content-end mb-2">
            <div className="d-flex flex-column gap-1">
              {rateUnavailable ? (
                <div className="d-flex align-items-center gap-1">
                  <span className="fs12px fw-semibold" style={{ color: "#FC555B" }}>
                    This currency is not currently listed for exchange
                  </span>
                </div>
              ) : (
                <>
                  <div className="d-flex align-items-center gap-1">
                    <span className="fs12px" style={{ color: "#7D7D7D" }}>
                      Exchange rate
                    </span>
                    <span
                      style={{ color: "#7D7D7D" }}
                      className="d-flex fs12px align-items-center justify-content-center"
                    >
                      |
                    </span>
                    <span className="fs12px" style={{ color: "#7D7D7D" }}>
                      {quote ? "Live" : indicativeRate ? "Indicative" : "Loading..."}
                    </span>
                    <img
                      src={refreshIcon}
                      alt="refresh"
                      className="cursor-pointer"
                    />
                  </div>
                  <div className="d-flex align-items-center gap-1 justify-content-end">
                    <span className="fs14px fw-semibold">
                      1 {sourceAccount?.currency || "AUD"} ={" "}
                      <span style={{ color: "#1bbb6b" }}>
                        {displayRate ? Number(displayRate).toFixed(6) : "..."}{" "}
                        {selectedBeneficiary?.bankAccounts?.[0]?.currency || "USD"}
                      </span>
                    </span>
                    <img src={downIcon} alt="down" className="cursor-pointer" />
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="col-lg-5 p-3">
            <div className="d-flex flex-column gap-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fs-5 fw-medium">Recipients</span>
                <Button2
                  icon={<img src={plusIcon} alt="add" />}
                  buttonName={"Add a Recipients"}
                  className={"btn-dark px-2 py-1"}
                  divClassName={"fs14px gap-2"}
                  onClick={() => navigate("/add-recipient")}
                />
              </div>

              <div className="new-transfer-inner-card mt-2 border p-3 rounded-2">
                <div className="d-flex flex-column gap-2">
                  <label className="fs12px">Search Recipient</label>
                  <Input
                    placeholder="Search by name or email"
                    prefix={<img src={searchIcon} alt="search" width={18} />}
                    className="p-3"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    allowClear
                  />

                  {searchTerm && filteredBeneficiaries.length > 0 && (
                    <div
                      className="border rounded-2 mt-1"
                      style={{ maxHeight: 220, overflowY: "auto" }}
                    >
                      {filteredBeneficiaries.map((b) => (
                        <div
                          key={b.id}
                          onClick={() => {
                            setSelectedBeneficiaryId(b.id);
                            setSearchTerm("");
                          }}
                          className={`p-2 cursor-pointer ${
                            selectedBeneficiaryId === b.id
                              ? "bg-light"
                              : ""
                          }`}
                          style={{ borderBottom: "1px solid #eee" }}
                        >
                          <div className="fs14px fw-medium">
                            {b.fullName || b.businessName}
                            <span
                              className="ms-2 fs12px"
                              style={{ color: "#7D7D7D" }}
                            >
                              ({b.accountType || "INDIVIDUAL"})
                            </span>
                          </div>
                          <div className="fs12px" style={{ color: "#7D7D7D" }}>
                            {b.email || b.mobile || "No contact"}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchTerm && filteredBeneficiaries.length === 0 && (
                    <span
                      className="fs12px"
                      style={{ color: "#FC555B" }}
                    >
                      No recipient found by name or email
                    </span>
                  )}

                  <label className="fs12px mt-2">Select Recipient</label>
                  <Select
                    value={selectedBeneficiaryId}
                    onChange={(e) => setSelectedBeneficiaryId(e.target.value)}
                    options={beneficiaries.map((b) => ({
                      name: `${b.fullName || b.businessName} (${b.countryCode || ""})`,
                      value: b.id,
                    }))}
                    className="w-100"
                    defaultValue="Select beneficiary"
                  />
                  {selectedBeneficiary && (
                    <div className="mt-2 d-flex flex-column">
                      <span className="fs12px fw-medium">
                        {selectedBeneficiary.payoutMethod} . {selectedBeneficiary.bankAccounts?.[0]?.currency || "USD"} . {selectedBeneficiary.bankAccounts?.[0]?.iban || "No IBAN"}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="d-flex flex-column gap-2">
                <div className="d-flex gap-3 ">
                  <div className=" d-flex flex-column gap-2 w-75">
                    <label className="fs12px">You send</label>
                    <Input
                      className="p-3 w-100"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                  </div>
                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">Currency</label>
                    <Select
                      value={sourceAccountId}
                      onChange={(e) => setSourceAccountId(e.target.value)}
                      options={accounts.map(a => ({
                        name: a.currency,
                        value: a.id
                      }))}
                      className="p-3 "
                      selectStyle={{ width: "100%" }}
                      defaultValue="Select currency"
                    />
                  </div>
                </div>
                {sourceAccount && (
                   <span className="fs12px">
                     Available: {Number(sourceAccount.availableBalance).toLocaleString()} {sourceAccount.currency}
                   </span>
                )}
              </div>

              <div className="d-flex flex-column gap-2">
                <span className="fs12px" style={{ color: "#7D7D7D" }}>
                  Transfer method
                </span>
                <div className="d-flex flex-nowrap gap-2">
                  {tranferMethodOptions.map(({ name, icon }, key) => (
                    <div
                      key={key}
                      className={`d-flex rounded-3 justify-content-between transfer-method-card ${
                        key === selectedCard.key ? "selected-card" : "border"
                      }  p-3`}
                    >
                      <div className="d-flex gap-2 align-items-center">
                        <img src={icon} alt="icon" width={25} />
                        <span className="fs14px fw-medium">{name}</span>
                      </div>
                      <Radio
                        width={"24px"}
                        accentColor={"green"}
                        name={"transfer-method"}
                        onClick={() => onCardSelect(key)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="d-flex flex-column gap-2">
                <Select defaultValue="Fees are Shared" className="p-3" options={[
                  { name: "Fees are Shared", value: "shared" },
                  { name: "I pay all fees", value: "sender" },
                  { name: "Recipient pays all fees", value: "receiver" },
                ]} />
                <div className="d-flex gap-1 align-items-center">
                  <img src={infoIcon} alt="info" />
                  <span className="fs12px" style={{ color: "#7D7D7D" }}>
                    You pay the outgoing transfer fees and the recipients pays
                    the and receiving bank fees
                  </span>
                </div>
              </div>

              <div className="d-flex flex-column gap-2">
                <div className="d-flex gap-3 ">
                  <div className="d-flex flex-column gap-2 w-75">
                    <label className="fs12px">Recipients get</label>
                    <Input
                      className="p-3 w-100"
                      placeholder="0.00"
                      value={
                        quote?.destAmount
                          ? Number(quote.destAmount).toFixed(2)
                          : amount && indicativeRate?.midRate
                          ? (Number(amount) * Number(indicativeRate.midRate)).toFixed(2)
                          : ""
                      }
                      readOnly
                    />
                  </div>
                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">Currency</label>
                    <Select
                      value={selectedBeneficiary?.bankAccounts?.[0]?.currency || "USD"}
                      className="p-3 "
                      selectStyle={{ width: "100%" }}
                      disabled
                    />
                  </div>
                </div>
                {quote && <FeeBreakdown quote={quote} kind="transfer" className="mt-2" />}
              </div>
            </div>
          </div>

          <div className="col-lg-6 p-3">
            <div className="d-flex flex-column gap-3">
              <span className="fs-5 fw-medium"> Transfer Details</span>

              <div className="d-flex flex-column gap-2">
                <label className="fs12px">Transfer date</label>
                <DatePicker placeholder={"January 26,2022"} className="p-3" />
                <span className="fs12px" style={{ color: "#7D7D7D" }}>
                  Swift Transfer usually arrive within 0-3 business days of this
                  date
                </span>
              </div>

              <div className="d-flex flex-column gap-2">
                <label className="fs12px">Purpose</label>
                <Select defaultValue="Select purpose" className="p-3" options={[
                  { name: "Family Support", value: "family" },
                  { name: "Salary", value: "salary" },
                  { name: "Bill Payment", value: "bill" },
                  { name: "Business", value: "business" },
                  { name: "Education", value: "education" },
                  { name: "Medical", value: "medical" },
                  { name: "Other", value: "other" },
                ]} />
                <span className="fs12px" style={{ color: "#7D7D7D" }}>
                  Please Specify purpose to ensure timely dispatch of transfer
                </span>
              </div>

              <div className="d-flex flex-column gap-2">
                <label className="fs12px">Transfer reference</label>
                <Input
                  placeholder="Payment for invoice #983263"
                  className="p-3"
                />
                <span className="fs12px" style={{ color: "#7D7D7D" }}>
                  Displayed on recipient's bank statement
                </span>
              </div>

              <div className="d-flex flex-column gap-2">
                <label className="fs12px">
                  Internal reference{" "}
                  <span style={{ color: "#7D7D7D" }}>(Optional)</span>
                </label>
                <Input placeholder="Job no #293736" className="p-3" />
                <span className="fs12px" style={{ color: "#7D7D7D" }}>
                  We won't send this to recipient. It needs to be district over
                  the last 7 days
                </span>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-md-end  justify-content-between p-2 gap-3">
            <Button1
              buttonName={"Save as Draft"}
              className={"btn-outline-success"}
            />
            <Button1
              buttonName={"Continue"}
              className="btn-dark "
              onClick={() => setIndex(1)}
            />
          </div>
        </div>
      ) : (
        <ExistingRecipientReview 
          onClick={setIndex} 
          data={{
            sourceAccountId,
            beneficiaryId: selectedBeneficiaryId,
            amount: Number(amount),
            quote,
            beneficiary: selectedBeneficiary,
            sourceAccount
          }}
        />
      )}
    </>
  );
};

export default ExistingRecipient;
